import { useEffect } from 'react';
import { useKeyboardStore } from '@/stores/keyboardStore';
import type { ShortcutCommand } from '@/types/keyboard';
import { keyboardRegistry } from '../keyboardRegistry';
import { matchesBinding, shouldIgnoreEvent } from '../keyboardUtils';
import { recordPointerPosition, clearPointerPosition } from '../pointerTarget';

export function useGlobalKeyboard() {
  const customBindings = useKeyboardStore((state) => state.customBindings);
  const disabledShortcuts = useKeyboardStore((state) => state.disabledShortcuts);
  const shortcutsEnabled = useKeyboardStore((state) => state.shortcutsEnabled);

  useEffect(() => {
    if (!shortcutsEnabled) return;

    const handleKeydown = (event: KeyboardEvent) => {
      // Skip if typing in input/textarea or contenteditable
      if (shouldIgnoreEvent(event)) {
        return;
      }

      // Resolve the matching command by priority (highest wins; ties fall back to
      // registration order) rather than blindly firing the first match. This makes
      // overlapping bindings (e.g. multiple Escape handlers) deterministic
      // (audit §2.17 #4).
      let chosen: ShortcutCommand | null = null;

      for (const command of keyboardRegistry.getAll()) {
        // Skip if globally disabled for this action
        if (disabledShortcuts.has(command.action)) continue;

        // A custom binding of `null` means the user explicitly CLEARED the
        // shortcut — that disables it. This is distinct from "no custom binding"
        // (undefined), which falls back to the default. The old `?? default`
        // treated a cleared null as "use default", silently re-enabling it
        // (audit §2.17 #3).
        const hasCustom = Object.prototype.hasOwnProperty.call(customBindings, command.action);
        const customBinding = hasCustom ? customBindings[command.action] : undefined;
        if (hasCustom && customBinding === null) continue;
        const binding = customBinding ?? command.defaultBinding;
        if (!binding) continue;

        if (!matchesBinding(event, binding)) continue;
        // Check if command is enabled (optional context condition)
        if (command.enabled && !command.enabled()) continue;

        if (!chosen || (command.priority ?? 0) > (chosen.priority ?? 0)) {
          chosen = command;
        }
      }

      if (chosen) {
        event.preventDefault();
        event.stopPropagation();
        chosen.handler();
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [customBindings, disabledShortcuts, shortcutsEnabled]);

  // Track the live pointer position so shortcuts can target the row physically
  // under the cursor at keypress time, robust to scroll (audit §2.17 #5).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleMouseMove = (event: MouseEvent) => {
      recordPointerPosition(event.clientX, event.clientY);
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearPointerPosition();
    };
  }, []);
}
